'use client';

/**
 * SubProcessorChangeBanner — top-of-page banner shown to authenticated
 * org members when there are unacknowledged sub-processor change events
 * for the active organization.
 *
 * Visible to every member of the org (read-only); the "Acknowledge"
 * button only appears to a workspace OWNER (the ORG_OWNER on an
 * Enterprise org) or a platform admin, mirroring the server gate
 * (gateOrgWriteAccess). Lower roles see the banner stay until an owner
 * acks — rendering the button to them would only surface a raw 403.
 *
 * Mounted in app/[locale]/layout.tsx so it appears across every
 * authenticated surface.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useApiQuery } from '@/hooks/useApiQuery';
import { acknowledgeSubprocessorChange, getPendingSubprocessorChanges } from '@/lib/api/admin-subprocessors';

interface SubProcessorChangeBannerProps {
  /** Optional override for testing — defaults to the active workspace. */
  organizationId?: string;
}

export function SubProcessorChangeBanner({ organizationId: overrideOrgId }: SubProcessorChangeBannerProps = {}) {
  const t = useTranslations('admin.subprocessor.banner');
  const { user } = useAuth();
  const { activeWorkspace } = useWorkspace();

  // The banner only applies to true Enterprise org workspaces.
  const orgId = overrideOrgId ?? activeWorkspace?.organizationId ?? null;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Fetch only when we have an authenticated user + org. The banner is
  // non-critical so we silently swallow errors via onError → empty list.
  const { data, refetch } = useApiQuery(
    ['subprocessor-pending', orgId ?? '', user?.id ?? ''],
    () => (orgId ? getPendingSubprocessorChanges(orgId) : Promise.resolve([])),
    {
      enabled: Boolean(orgId && user),
      // Suppress fetch errors; the banner is non-critical.
      onError: () => {},
    },
  );

  const pending = useMemo(() => (dismissed ? [] : (data ?? [])), [data, dismissed]);

  const refresh = useCallback(() => refetch(), [refetch]);

  const onAcknowledge = useCallback(
    async (changeId: string) => {
      if (!orgId) return;
      setBusy(true);
      setError(null);
      try {
        await acknowledgeSubprocessorChange(orgId, changeId);
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'unknown_error';
        setError(message);
      } finally {
        setBusy(false);
      }
    },
    [orgId, refresh],
  );

  const head = pending && pending.length > 0 ? pending[0] : null;

  // Mirror the server gate (gateOrgWriteAccess): only a platform admin or
  // the workspace OWNER (the ORG_OWNER on an Enterprise org) may acknowledge.
  // Lower roles see the banner read-only — rendering the button to them only
  // surfaces a raw 403 (org_owner_required) on click.
  const canAcknowledge = user?.role === 'ADMIN' || activeWorkspace?.memberRole === 'OWNER';

  if (!orgId || !user || !head) return null;

  const message = t('message', {
    kind: head.changeKind,
    subprocessor: head.subprocessorId,
  });

  return (
    <div
      role="status"
      className="flex items-start gap-3 border-b border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-700 dark:text-amber-300"
      data-testid="subprocessor-change-banner"
    >
      <span aria-hidden>
        <AlertTriangle size={16} />
      </span>
      <p className="flex-1 leading-snug">
        <strong>{t('title')}</strong>
        {' — '}
        <span>{message}</span>
        {pending.length > 1 ? (
          <span className="ml-2 opacity-75">{t('moreCount', { count: pending.length - 1 })}</span>
        ) : null}
      </p>
      {canAcknowledge ? (
        <button
          type="button"
          onClick={() => void onAcknowledge(head.id)}
          disabled={busy}
          className="rounded-md border border-current px-2 py-1 text-xs font-medium text-current hover:bg-current/10 disabled:opacity-50"
        >
          {t('acknowledgeButton')}
        </button>
      ) : null}
      {error ? (
        <span className="ml-2 text-xs opacity-90" role="alert">
          {error}
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="opacity-70 hover:opacity-100"
        aria-label={t('dismissUntilNextChange')}
      >
        <X size={14} />
      </button>
    </div>
  );
}

export default SubProcessorChangeBanner;
